import { Routes } from '@dtx-company/true-common/src/constants/routes'
import {
  type SignUpLocation,
  type User,
  signupWithEmailAndPassword
} from '@dtx-company/ithaca-sdk/src'
import {
  auth_signup_form_emailpassword_failure,
  auth_signup_form_emailpassword_started,
  auth_signup_form_emailpassword_success
} from '../../constants/authHeapEvents'
import { fireAnalyticsEvent } from '../../event-tracking/helpers/fireAnalyticsEvent'
import { logger } from '@dtx-company/logger'
import { updateHubspotCommunicationPreferences } from '../../utils/notifications'
import { useCallback, useMemo } from 'react'
import { useCohort } from '@dtx-company/inter-app/src/hooks/cohorts/useCohort'
import { useRouter } from 'next/router'
import type { AuthenticationFormFields } from '../../components/Authentication/AuthenticationForm.types'
import type { Domains } from '@dtx-company/true-common/src/types/domains'

export interface UseSignupProps {
  values: AuthenticationFormFields
  product: Domains
  shouldForceActivate?: boolean
}

export function useSignup(): ({ values, product, shouldForceActivate }: UseSignupProps) => Promise<{
  user: User
  errors: string[]
  accessToken?: string
}> {
  const { name: cohortName } = useCohort()
  const { pathname } = useRouter()

  const signupLocation: SignUpLocation = useMemo(() => {
    switch (pathname) {
      case Routes.SIGN_UP:
        return 'signUpPage'
      case Routes.FREE_QR_CODE_GENERATOR:
        return 'loggedOutCodeGenerator'
      case Routes.PAGES:
        return 'flowpage_pages'
      default:
        return 'unknown'
    }
  }, [pathname])

  return useCallback(
    async ({ values, product, shouldForceActivate = false }) => {
      try {
        const { email, noMarketingEmails } = values

        fireAnalyticsEvent(auth_signup_form_emailpassword_started, {
          page: pathname
        })

        const { errors, shouldActivate, accessToken } = await signupWithEmailAndPassword({
          ...values,
          product,
          cohort: cohortName,
          signupLocation,
          shouldForceActivate
        })

        if (accessToken) {
          fireAnalyticsEvent(auth_signup_form_emailpassword_success, {
            cohort: cohortName,
            page: pathname
          })

          if (noMarketingEmails) {
            fireAnalyticsEvent('userOptedOutOfMarketingEmails', {
              page: pathname
            })
            await updateHubspotCommunicationPreferences({
              email
            })
          }

          // turn this on if re-enabling Flowpage signup onboarding
          // if (pathname === Routes.PAGES && user.email) {
          //   events.userCreatedFlowpageAccount(user.email)
          //   dispatch(checkUserHasReachedMaxPages())
          // }
        }

        const user: User = { email, shouldActivate }

        return { user, errors, accessToken }
      } catch (e) {
        fireAnalyticsEvent(auth_signup_form_emailpassword_failure, {
          page: pathname
        })

        logger.logError(e, { technicalArea: 'general' })

        return { user: { email: null, shouldActivate: false }, errors: [] }
      }
    },
    [cohortName, signupLocation, pathname]
  )
}
